import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import { Play, Pause, Trophy } from "lucide-react";
import { toast } from "sonner";

function MatchCell({ side, beat, votes, onVote, disabled, winnerId }) {
  const { play, track, playing } = usePlayer();
  const isActive = track && beat && track.id === beat.id;
  const isPlaying = isActive && playing;
  const isWinner = winnerId && beat && winnerId === beat.id;

  if (!beat) {
    return (
      <div className="border border-dashed border-border p-3 text-center font-mono-acc text-[10px] text-muted-foreground min-h-[80px] flex items-center justify-center">
        TBD
      </div>
    );
  }
  return (
    <div className={`border ${isWinner ? "border-secondary" : "border-border"} bg-card p-3 flex items-center gap-3`}>
      <button
        data-testid={`match-play-${side}-${beat.id}`}
        onClick={() => play(beat)}
        className="w-10 h-10 border border-border flex items-center justify-center hover:bg-primary hover:border-primary hover:text-white shrink-0"
      >
        {isPlaying ? <Pause size={14} /> : <Play size={14} />}
      </button>
      {beat.cover_path && (
        <img src={fileUrl(beat.cover_path)} alt="" className="w-10 h-10 object-cover border border-border" />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-display text-sm truncate">{beat.title}</p>
        <p className="font-mono-acc text-[9px] text-muted-foreground">@{beat.username}</p>
      </div>
      <div className="text-right">
        <p className="font-display text-2xl text-primary">{votes}</p>
        <button
          data-testid={`match-vote-${side}-${beat.id}`}
          onClick={onVote}
          disabled={disabled}
          className="font-mono-acc text-[9px] border border-border px-2 py-1 mt-1 hover:bg-primary hover:text-white hover:border-primary disabled:opacity-30"
        >
          Vote
        </button>
      </div>
      {isWinner && <Trophy size={16} className="text-secondary shrink-0" />}
    </div>
  );
}

export default function TournamentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [beats, setBeats] = useState({}); // id -> beat
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/tournaments/${id}`);
      setT(data);
      // collect beat ids
      const ids = new Set();
      data.rounds.forEach((r) => r.forEach((m) => { if (m.beat_a) ids.add(m.beat_a); if (m.beat_b) ids.add(m.beat_b); }));
      const fetched = {};
      await Promise.all(
        Array.from(ids).map(async (bid) => {
          try { const r = await api.get(`/beats/${bid}`); fetched[bid] = r.data; } catch {}
        })
      );
      setBeats(fetched);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const vote = async (roundIdx, matchId, side) => {
    if (!user || typeof user !== "object") { toast.error("Login to vote"); return; }
    try {
      const { data } = await api.post(
        `/tournaments/${id}/vote?round_idx=${roundIdx}&match_id=${matchId}&side=${side}`
      );
      setT(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vote failed");
    }
  };

  const roundLabels = useMemo(() => {
    if (!t) return [];
    const total = t.rounds.length;
    return t.rounds.map((_, i) => {
      if (i === total - 1) return "FINAL";
      if (i === total - 2) return "SEMIS";
      if (i === total - 3) return "QUARTERS";
      return `ROUND ${i + 1}`;
    });
  }, [t]);

  if (loading) return <div className="p-12 font-mono-acc text-sm text-muted-foreground"><span className="rec-dot mr-2" /> Loading bracket...</div>;
  if (!t) return <div className="p-12">Tournament not found</div>;

  return (
    <div data-testid="tournament-detail-page" className="px-6 md:px-12 py-12 max-w-7xl mx-auto">
      <div className="flex items-baseline gap-4 flex-wrap">
        <div className={`font-mono-acc text-xs ${t.status === "complete" ? "text-secondary" : "text-primary"}`}>
          {t.status === "complete" ? "■ COMPLETE" : "● LIVE"}
        </div>
        <h1 className="font-display text-5xl md:text-6xl">{t.name}</h1>
      </div>
      {t.description && <p className="mt-3 text-muted-foreground max-w-2xl">{t.description}</p>}

      {t.champion && beats[t.champion] && (
        <div className="mt-8 border-2 border-secondary bg-secondary/10 p-6 flex items-center gap-4" data-testid="champion-banner">
          <Trophy className="text-secondary" size={32} />
          <div>
            <div className="font-mono-acc text-[10px] text-secondary">CHAMPION</div>
            <p className="font-display text-3xl">{beats[t.champion].title}</p>
            <p className="font-mono-acc text-[10px] text-muted-foreground">@{beats[t.champion].username}</p>
          </div>
        </div>
      )}

      <div className="mt-12 grid gap-6 md:gap-10" style={{ gridTemplateColumns: `repeat(${t.rounds.length}, minmax(260px, 1fr))`, overflowX: "auto" }}>
        {t.rounds.map((round, ri) => (
          <div key={ri} className="space-y-4 min-w-[260px]">
            <div className="font-mono-acc text-[10px] text-muted-foreground border-b border-border pb-2">{roundLabels[ri]}</div>
            <div className="space-y-6" style={{ paddingTop: ri * 8 }}>
              {round.map((m) => {
                const voted = user && typeof user === "object" && m.voters?.includes(user.id);
                return (
                  <div key={m.match_id} className="space-y-2">
                    <MatchCell
                      side="a"
                      beat={m.beat_a ? beats[m.beat_a] : null}
                      votes={m.votes_a}
                      winnerId={m.winner}
                      disabled={voted || !m.beat_a || !m.beat_b}
                      onVote={() => vote(ri, m.match_id, "a")}
                    />
                    <div className="text-center font-mono-acc text-[10px] text-muted-foreground">VS</div>
                    <MatchCell
                      side="b"
                      beat={m.beat_b ? beats[m.beat_b] : null}
                      votes={m.votes_b}
                      winnerId={m.winner}
                      disabled={voted || !m.beat_a || !m.beat_b}
                      onVote={() => vote(ri, m.match_id, "b")}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
